import Layout from 'antd/lib/layout';
import { Content } from 'antd/lib/layout/layout';
import * as React from 'react';
import { AppRouteComponentMatchParams, ReleaseStepComponentProps } from '../../typescriptHelpers';
import Button from 'antd/lib/button';
import Space from 'antd/lib/space';
import Text from 'antd/lib/typography/Text';
import styles from '../styles/appStyles';
import { antGrey, runwayColors } from '../styles/globalStyles';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { createAppSettingsRoute, SettingsScrollTo } from '../../helpers/routeHelpers';
import { AppSettingsLocationState } from '../../helpers/helperTypes';
import { AutomationStatus, AutomationRun } from '../../types';
import {
    CheckOutlined,
    CloseOutlined,
    ExclamationOutlined,
    MinusOutlined,
    HourglassOutlined,
    ThunderboltFilled,
    EditOutlined,
    PauseOutlined,
    WarningOutlined,
    RedoOutlined,
} from '@ant-design/icons';
import { colorForAutomationStatus } from '../../helpers/displayHelpers';
import AutomationRunsList from './AutomationRunsList';
import { AppAutomationSettings } from './AppAutomationSettings';
import { RootState } from '../../reducers/reducers';
import { useSelector } from 'react-redux';
import { globalMessage } from '../../utils/globalMessage';
import { selectSortedFilteredAutomations } from '@/selectors/memoizedSelectors';

export interface AutomationTabProps extends ReleaseStepComponentProps {
    automationRuns: AutomationRun[];
    isReleaseStep: boolean;
}

const AutomationTab: React.FC<AutomationTabProps> = (props) => {
    const [showAutomationSettings, setShowAutomationSettings] = React.useState(false);
    const [automationIdSelectedForSettings, setAutomationIdSelectedForSettings] = React.useState(null);

    const match = useRouteMatch<AppRouteComponentMatchParams>();
    const history = useHistory();
    const appSettingAutomations = useSelector((state: RootState) =>
        selectSortedFilteredAutomations(state, props.appId),
    );

    const { automationRuns } = props;
    const hasAutomations = automationRuns?.length > 0;

    const onShowAutomationSettings = (automationId: string) => {
        const automation = appSettingAutomations.find((automation) => automation.id === automationId);

        if (!automation) {
            globalMessage.error(`Automation with id ${automationId} not found`);

            return;
        }

        setAutomationIdSelectedForSettings(automationId);
        setShowAutomationSettings(true);
    };

    const onCloseAutomationSettings = () => {
        setShowAutomationSettings(false);
    };

    return (
        <Layout style={{ height: '100%' }}>
            <Content style={!hasAutomations ? styles.centerVerticalAndHorizontal : {}}>
                {!hasAutomations && (
                    <div style={{ display: 'flex' }}>
                        <Space direction="vertical" align="center" size="large">
                            <ThunderboltFilled style={{ fontSize: 50, color: antGrey.grey5 }} />
                            <Text style={{ fontSize: 20, color: antGrey.grey6 }}>No automations yet.</Text>
                            <Button
                                icon={<EditOutlined style={{ color: runwayColors.info }} />}
                                onClick={() => {
                                    const appSettingsRoute = createAppSettingsRoute(
                                        match.params.orgIdParam,
                                        match.params.appIdParam,
                                    );
                                    const state: AppSettingsLocationState = {
                                        scrollTo: SettingsScrollTo.Automations,
                                    };

                                    history.push(appSettingsRoute, state);
                                }}
                            >
                                App settings
                            </Button>
                        </Space>
                    </div>
                )}
                {hasAutomations && (
                    <AutomationRunsList {...props} onShowAutomationSettings={onShowAutomationSettings} />
                )}
                {automationIdSelectedForSettings && (
                    <AppAutomationSettings
                        onAutomationSelected={(automationId) => setAutomationIdSelectedForSettings(automationId)}
                        orgId={props.orgId}
                        automationId={automationIdSelectedForSettings}
                        appId={props.appId}
                        onClose={onCloseAutomationSettings}
                        open={showAutomationSettings}
                        onAfterClose={() => {
                            setAutomationIdSelectedForSettings(null);
                        }}
                        releaseId={props.releaseId}
                    />
                )}
            </Content>
        </Layout>
    );
};

export default AutomationTab;

export const borderedIconForAutomation = (status: AutomationStatus, isLifecycle: boolean, isEnabled: boolean) => {
    const circleStyle: React.CSSProperties = {
        borderWidth: 1,
        borderStyle: 'solid',
        borderRadius: 16,
        height: 32,
        width: 32,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: 16,
        flexShrink: 0,
    };
    const iconSize = 16;
    const color = isEnabled ? colorForAutomationStatus(status, isLifecycle) : antGrey.grey6;
    const icon =
        isEnabled ?
            iconForAutomationStatus(status, color, iconSize)
        :   <MinusOutlined style={{ fontSize: iconSize, color }} />;

    return <div style={{ ...circleStyle, borderColor: color }}>{icon}</div>;
};

export const iconForAutomationStatus: (status: AutomationStatus, color: string, size: number) => React.ReactNode = (
    status,
    color,
    size,
) => {
    let icon = null;

    switch (status) {
        case AutomationStatus.Active:
        case AutomationStatus.Success:
            icon = <CheckOutlined style={{ fontSize: size, color }} />;
            break;
        case AutomationStatus.Failed:
            icon = <CloseOutlined style={{ fontSize: size, color }} />;
            break;
        case AutomationStatus.Pending:
            icon = <HourglassOutlined style={{ fontSize: size, color }} />;
            break;
        case AutomationStatus.Inactive:
        case AutomationStatus.Skipped:
            icon = <MinusOutlined style={{ fontSize: size, color }} />;
            break;
        case AutomationStatus.Incomplete:
            icon = <WarningOutlined style={{ fontSize: size, color }} />;
            break;
        case AutomationStatus.Paused:
            icon = <PauseOutlined style={{ fontSize: size, color }} />;
            break;
        case AutomationStatus.Blocked:
            icon = <ExclamationOutlined style={{ fontSize: size, color }} />;
            break;
        case AutomationStatus.Retrying:
            icon = <RedoOutlined style={{ fontSize: size, color }} />;
            break;
    }

    return icon;
};
